import React, { useEffect, useRef, useState } from 'react';
import identity from 'lodash/identity';
import uniqueId from 'lodash/uniqueId';
import debounce from 'lodash/debounce';
import { validateEmail } from '../../validators';
import FormControlError from '../FormControlError';

import './InputField.less';

/**
 *
 * @param {{
 * ariaLabel?:string;
 * ariaDescribedBy?:string;
 * id?:string;
 * title?:string;
 * name?:string;
 * value?:string;
 * defaultValue?:string;
 * required?:boolean;
 * autofocus?:boolean;
 * disabled?:boolean;
 * autoComplete?:string;
 * placeholder?:string;
 * onChange?(name:string, value:string):void;
 * limit?:number;
 * inputType?:string;
 * customErrorMessage?:React.ReactNode;
 * customErrorPrefix?:string|null;
 * customValidator?(params:{value:string, errorMessage:React.ReactNode}):React.ReactNode;
 * postControlSlot?:React.ReactNode|(()=>React.ReactNode);
 * alwaysDisplayErrorMessage?:boolean;
 * onValidityChange?(params:{errorMessage:React.ReactNode}):void;
 * debounceOnChange?:number;
 * }} props
 * @returns {React.ReactElement}
 */
export const InputField = props => {
  const {
    onChange,
    customErrorMessage,
    value = '',
    defaultValue = void 0,
    limit = 0,
    autofocus = false,
    required = false,
    title = '',
    name,
    inputType = 'text',
    ariaLabel,
    ariaDescribedBy,
    placeholder,
    autoComplete,
    disabled = false,
    customValidator,
    customErrorPrefix,
    postControlSlot,
    alwaysDisplayErrorMessage,
    onValidityChange,
    debounceOnChange = 0,
  } = props;
  const inputFieldRef = useRef(/** @type {null|HTMLInputElement} */ (null));
  const [currentValue, setCurrentValue] = useState(value ?? '');
  const [isDirty, setDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState(/** @type {React.ReactNode} */ (null));
  const hasLimits = limit > 0;
  const uiLibClassNames = ['form-input'];
  const inputClasses = [
    `lsn-control lsn-control-input lsn-control-input-${inputType}`,
    ...uiLibClassNames,
    errorMessage ? 'has-error' : '',
    hasLimits ? 'with-couter' : '',
  ]
    .filter(identity)
    .join(' ');
  const [controlUID] = useState(props.id || uniqueId(`lsn_ctrl_`));

  /**
   *
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleFocus = e => {
    setDirty(true);
  };

  const onChangeDebounced = useRef(
    debounce((name, value) => {
      onChange?.(name ?? '', value);
    }, debounceOnChange ?? 0)
  );

  /**
   *
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleChange = e => {
    const value = e.target.value;
    setDirty(true);
    setCurrentValue(value);
    debounceOnChange ? onChangeDebounced.current?.(name ?? '', value) : onChange?.(name ?? '', value);
  };

  /**
   *
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleBlur = e => {
    setDirty(true);
  };

  /**
   *
   * @param {string} v
   * @returns {React.ReactNode}
   */
  const _getNativeErrorMessage = v => {
    const value = (v || '').trim();
    let errorMessage;

    if (!value && required === true) {
      errorMessage = `${customErrorPrefix ?? title} is required`;
    } else if (inputType === 'email' && !validateEmail(value)) {
      errorMessage = `${customErrorPrefix ?? title} is invalid`;
    }

    errorMessage = customValidator ? customValidator({ value, errorMessage }) ?? '' : errorMessage;
    return errorMessage ?? '';
  };

  /**
   *
   * @param {string} v
   */
  const refreshErrorMessage = v => {
    const isManualErrControl = 'customErrorMessage' in props;
    const errorMessage = isManualErrControl
      ? customErrorMessage ?? ''
      : disabled
      ? ''
      : isDirty
      ? _getNativeErrorMessage(v)
      : '';
    setErrorMessage(errorMessage);
    // report validity to this control
    errorMessage
      ? inputFieldRef.current?.setCustomValidity(
          typeof errorMessage === 'string' ? errorMessage : 'This field is invalid'
        )
      : inputFieldRef.current?.setCustomValidity('');
  };

  // auto-focus
  useEffect(() => {
    if (inputFieldRef.current && autofocus) {
      inputFieldRef.current.focus();
      setDirty(!!value || isDirty);
    }
  }, [inputFieldRef.current]);

  // refresh value
  useEffect(() => {
    setCurrentValue(value ?? '');
    setDirty(!!value || isDirty);
  }, [value]);

  // update the vaidity when control gets dirty or the value is being changed
  useEffect(() => {
    refreshErrorMessage(currentValue ?? '');
  }, [isDirty, currentValue, required, disabled, customErrorMessage]);

  useEffect(() => {
    onValidityChange?.({ errorMessage });
  }, [errorMessage]);

  return (
    <>
      <input
        aria-label={
          hasLimits ? `${ariaLabel ?? title} ${currentValue.length} character out of ${limit}` : title ?? ariaLabel
        }
        aria-describedby={ariaDescribedBy}
        aria-required={required}
        aria-invalid={!!errorMessage}
        aria-errormessage={errorMessage ? `${controlUID}_error_msg` : void 0}
        id={controlUID}
        ref={inputFieldRef}
        title={title || ariaLabel || 'input filed'}
        className={inputClasses}
        name={name}
        type={inputType}
        {
          ... defaultValue ? { defaultValue } : { value: currentValue }
        }
        placeholder={placeholder}
        autoComplete={autoComplete}
        maxLength={limit || void 0}
        disabled={disabled}
        required={required}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onInvalid={handleBlur}
      />
      <div className={['control-error-wrapper', hasLimits ? 'with-counter' : ''].filter(identity).join(' ')}>
        {required || alwaysDisplayErrorMessage ? (
          <FormControlError id={`${controlUID}_error_msg`} show={!!errorMessage}>
            {errorMessage}
          </FormControlError>
        ) : null}
        {hasLimits ? (
          <div
            id={`${controlUID}_counter`}
            className="form-input-counter"
            role={limit <= currentValue.length ? 'alert' : 'term'}
            aria-label={`${limit - currentValue.length} characters out of ${limit}`}
          >
            {`${limit - currentValue.length}/${limit}`}
          </div>
        ) : null}
      </div>
      {typeof postControlSlot === 'function' ? postControlSlot() : postControlSlot}
    </>
  );
};

export const createInputFieldId = () => uniqueId('lsn_input_');

export default InputField;
